import type { JSX } from 'react';
import { useMemo, useState } from 'react';

import { Braces, ChevronDown, ChevronRight, Plus, Sparkles, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

import type { LeafValue, ToolCatalogEntry } from '../../model/toolCatalog';
import { coerceLeafInput, flattenLeafParams, matchSeedCaseLabel, setLeafParam } from '../../model/toolCatalog';

const MAX_FLAT_PARAMS = 40;

interface ScenarioToolStubsFieldProps {
  value: string;
  onChange: (text: string) => void;
  tools: ToolCatalogEntry[];
}

interface AddStubDraft {
  toolName: string;
  caseKey: string;
}

const parseStubs = (text: string): Record<string, unknown> | null => {
  if (text.trim().length === 0) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
};

const serializeStubs = (stubs: Record<string, unknown>): string =>
  Object.keys(stubs).length === 0 ? '' : JSON.stringify(stubs, null, 2);

const ScenarioToolStubsField = ({ value, onChange, tools }: ScenarioToolStubsFieldProps): JSX.Element => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [jsonMode, setJsonMode] = useState<Set<string>>(new Set());
  const [jsonDrafts, setJsonDrafts] = useState<Record<string, string>>({});
  const [addDraft, setAddDraft] = useState<AddStubDraft | null>(null);

  const stubs = useMemo(() => parseStubs(value), [value]);
  const toolsByName = useMemo(() => new Map(tools.map((tool) => [tool.name, tool])), [tools]);

  const updateStubs = (next: Record<string, unknown>): void => {
    onChange(serializeStubs(next));
  };

  const toggleIn = (set: Set<string>, key: string): Set<string> => {
    const next = new Set(set);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    return next;
  };

  if (stubs === null) {
    return (
      <div className="flex flex-col gap-1.5">
        <Textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          rows={4}
          className="max-h-48 font-mono text-xs"
        />
        <p className="m-0 text-xs text-error">JSON inválido: corrígelo para volver al editor por tool.</p>
      </div>
    );
  }

  const stubEntries = Object.entries(stubs);
  const addableTools = tools.filter((tool) => !(tool.name in stubs));
  const addDraftTool = addDraft ? toolsByName.get(addDraft.toolName) ?? null : null;
  const addDraftCases = addDraftTool?.seed?.cases ?? [];

  const handleAddStub = (): void => {
    if (!addDraft) return;
    const seedCase = addDraftCases.find((c) => c.key === addDraft.caseKey);
    const snapshot: unknown = seedCase ? seedCase.response : {};
    updateStubs({ ...stubs, [addDraft.toolName]: snapshot });
    setExpanded((prev) => new Set(prev).add(addDraft.toolName));
    setAddDraft(null);
  };

  const applySeedCase = (toolName: string, caseKey: string): void => {
    const seedCase = toolsByName.get(toolName)?.seed?.cases.find((c) => c.key === caseKey);
    if (!seedCase) return;
    updateStubs({ ...stubs, [toolName]: seedCase.response });
    setJsonDrafts((prev) => ({ ...prev, [toolName]: JSON.stringify(seedCase.response, null, 2) }));
  };

  const removeStub = (toolName: string): void => {
    const { [toolName]: _removed, ...rest } = stubs;
    updateStubs(rest);
  };

  const setLeaf = (toolName: string, path: string, leaf: LeafValue): void => {
    updateStubs({ ...stubs, [toolName]: setLeafParam(stubs[toolName], path, leaf) });
  };

  const handleJsonDraftChange = (toolName: string, text: string): void => {
    setJsonDrafts((prev) => ({ ...prev, [toolName]: text }));
    try {
      const parsed: unknown = JSON.parse(text);
      updateStubs({ ...stubs, [toolName]: parsed });
    } catch {
      // Invalid draft: keep the last applied value until the JSON parses.
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        className={
          stubEntries.length > 0 ? 'relative flex max-h-[300px] flex-col gap-2 overflow-y-auto pr-1' : 'hidden'
        }
      >
        {stubEntries.map(([toolName, stubValue]) => {
          const tool = toolsByName.get(toolName) ?? null;
          const seed = tool?.seed ?? null;
          const isExpanded = expanded.has(toolName);
          const isJsonMode = jsonMode.has(toolName);
          const seedLabel = matchSeedCaseLabel(seed, stubValue);
          const leafParams = flattenLeafParams(stubValue);
          const flatEditable = leafParams.length > 0 && leafParams.length <= MAX_FLAT_PARAMS;
          const jsonDraft = jsonDrafts[toolName] ?? JSON.stringify(stubValue, null, 2);

          return (
            <div
              key={toolName}
              className="flex flex-col gap-2.5 rounded-[10px] border border-border-subtle bg-bg-surface p-3"
            >
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  className="flex min-w-0 items-center gap-2 text-left"
                  onClick={() => setExpanded((prev) => toggleIn(prev, toolName))}
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? (
                    <ChevronDown className="size-3.5 shrink-0 text-text-muted" aria-hidden />
                  ) : (
                    <ChevronRight className="size-3.5 shrink-0 text-text-muted" aria-hidden />
                  )}
                  <span className="truncate font-mono text-xs font-medium text-text-default">{toolName}</span>
                  {seed ? (
                    <span className="shrink-0 rounded-[10px] bg-brand-muted px-2 py-0.5 text-[10px] font-semibold text-brand">
                      schema v{seed.schemaVersion}
                    </span>
                  ) : null}
                  <span className="truncate text-[11px] text-text-muted">
                    {seedLabel ? `Seed: ${seedLabel}` : 'Editado'}
                  </span>
                </button>
                <div className="flex shrink-0 items-center gap-1">
                  {isExpanded ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 text-xs text-text-muted"
                      onClick={() => setJsonMode((prev) => toggleIn(prev, toolName))}
                    >
                      <Braces className="size-3.5" aria-hidden />
                      {isJsonMode ? 'Editar campos' : 'Editar como JSON'}
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => removeStub(toolName)}
                    aria-label={`Eliminar stub ${toolName}`}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </div>
              </div>

              {isExpanded ? (
                <>
                  {seed && seed.cases.length > 0 ? (
                    <div className="flex items-center gap-2">
                      <span className="shrink-0 text-[11px] text-text-muted">Responde con:</span>
                      <Select onValueChange={(caseKey) => applySeedCase(toolName, caseKey)}>
                        <SelectTrigger size="sm" className="min-w-[180px] text-xs">
                          <SelectValue placeholder={seedLabel ?? 'Elegir caso…'} />
                        </SelectTrigger>
                        <SelectContent>
                          {seed.cases.map((seedCase) => (
                            <SelectItem key={seedCase.key} value={seedCase.key}>
                              {seedCase.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : null}

                  {!isJsonMode && flatEditable ? (
                    <div className="flex flex-col gap-1.5">
                      {leafParams.map((param) => (
                        <div key={param.path} className="flex items-start gap-2.5">
                          <span className="w-[45%] truncate pt-1.5 font-mono text-[11px] text-text-muted">
                            {param.path}
                          </span>
                          {typeof param.value === 'boolean' ? (
                            <Switch
                              checked={param.value}
                              onCheckedChange={(checked) => setLeaf(toolName, param.path, checked)}
                              aria-label={param.path}
                            />
                          ) : typeof param.value === 'number' ? (
                            <Input
                              value={String(param.value)}
                              onChange={(event) =>
                                setLeaf(toolName, param.path, coerceLeafInput(event.target.value, param.value))
                              }
                              className="h-7 flex-1 font-mono text-xs"
                              inputMode="decimal"
                              aria-label={param.path}
                            />
                          ) : (
                            <Textarea
                              value={param.value === null ? '' : param.value}
                              onChange={(event) =>
                                setLeaf(toolName, param.path, coerceLeafInput(event.target.value, param.value))
                              }
                              rows={1}
                              className="field-sizing-content max-h-[5.5rem] min-h-7 flex-1 resize-none overflow-y-auto py-1 text-xs"
                              aria-label={param.path}
                            />
                          )}
                        </div>
                      ))}
                      <p className="m-0 flex items-center gap-1.5 text-[11px] text-text-muted">
                        <Sparkles className="size-3" aria-hidden />
                        Ejemplo pre-generado desde el seed de la tool · edita solo los campos que te importan
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1">
                      <Textarea
                        value={jsonDraft}
                        onChange={(event) => handleJsonDraftChange(toolName, event.target.value)}
                        rows={6}
                        className="max-h-48 font-mono text-xs"
                        aria-label={`JSON del stub ${toolName}`}
                      />
                      {!flatEditable && !isJsonMode ? (
                        <p className="m-0 text-[11px] text-text-muted">
                          Estructura compleja o vacía: se edita como JSON.
                        </p>
                      ) : null}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          );
        })}
      </div>

      {addDraft ? (
        <div className="flex flex-wrap items-center gap-2 rounded-[10px] border border-dashed border-border-subtle p-3">
          <Select
            value={addDraft.toolName || undefined}
            onValueChange={(toolName) => {
              const seedCases = toolsByName.get(toolName)?.seed?.cases ?? [];
              setAddDraft({ toolName, caseKey: seedCases.find((c) => c.key === 'success')?.key ?? '' });
            }}
          >
            <SelectTrigger size="sm" className="min-w-[220px] text-xs">
              <SelectValue placeholder="Tool…" />
            </SelectTrigger>
            <SelectContent>
              {addableTools.map((tool) => (
                <SelectItem key={tool.name} value={tool.name}>
                  <span className="font-mono text-xs">{tool.name}</span>
                  {tool.availableInProject === false ? (
                    <span className="text-text-muted"> · no disponible en este proyecto</span>
                  ) : null}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {addDraftCases.length > 0 ? (
            <Select
              value={addDraft.caseKey || undefined}
              onValueChange={(caseKey) => setAddDraft((prev) => (prev ? { ...prev, caseKey } : prev))}
            >
              <SelectTrigger size="sm" className="min-w-[180px] text-xs">
                <SelectValue placeholder="Caso…" />
              </SelectTrigger>
              <SelectContent>
                {addDraftCases.map((seedCase) => (
                  <SelectItem key={seedCase.key} value={seedCase.key}>
                    {seedCase.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : addDraft.toolName ? (
            <span className="text-[11px] text-text-muted">Sin seed: se agrega un stub vacío {'{}'}</span>
          ) : null}
          <div className="ml-auto flex items-center gap-1.5">
            <Button type="button" variant="ghost" size="sm" onClick={() => setAddDraft(null)}>
              Cancelar
            </Button>
            <Button type="button" size="sm" onClick={handleAddStub} disabled={!addDraft.toolName}>
              Agregar
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddDraft({ toolName: '', caseKey: '' })}
            disabled={addableTools.length === 0}
          >
            <Plus className="size-4" aria-hidden />
            Agregar stub
          </Button>
        </div>
      )}
    </div>
  );
};

export default ScenarioToolStubsField;
